import React, { useMemo } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { MaterialReactTable } from "material-react-table";
import { mkConfig, generateCsv, download } from "export-to-csv";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/AddCircle";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TableMaestros from "verticales/TableMaestros.tsx";
import Footer from "examples/Footer";
import UtilModal from "verticales/UtilModal";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import AppLoading from "verticales/AppLoading.tsx";
import { Card } from "@mui/material";
import {
  IconButton,
  Tooltip,
  Box,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import ViewNewEdit from "./ViewNewEdit.js";
import fetchData from "verticales/FetchData";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ",",
  useKeysAsHeaders: true,
});

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 54,
  pl: 2,
  pr: 2,
  pt: 0,
  pb: 2,
};

const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
const context = "ec2-get-agente";

const Example = (props: any) => {
  const [data, setData] = React.useState([]);
  const [blocking, setBlocking] = React.useState(false);
  const [dataInstance, setDataInstance] = React.useState([]);
  const [msgAction, setMsgAction] = React.useState(context);
  const [openModal, setOpenModal] = React.useState(false);
  const [cuentaSelect, setCuentaSelect] = React.useState({});
  const [rowSelect, setRowSelect] = React.useState({});
  const [openMonitor, setOpenMonitor] = React.useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState(null);

  const handleOpenMonitor = (row) => {
    setOpenMonitor(true);
    setRowSelect(row);
  };

  const renderView = () => {
    return (
      <ViewNewEdit
        row={rowSelect}
        handleCloseModal={handleCloseModal}
        dataInstance={dataInstance}
        handleCloseMonitor={handleCloseMonitor}
        cuentaSelect={cuentaSelect}
      />
    );
  };

  const handleOpenModal = (row) => {
    setOpenModal(true);
    setRowSelect(row);
  };

  const handleCloseMonitor = async (refresh) => {
    setOpenMonitor(false);
    if (refresh && refresh === true) {
      await loadList(cuentaSelect);
    }
  };

  const handleCloseModal = async (refresh) => {
    setOpenModal(false);
    if (refresh) {
      await loadList(cuentaSelect);
    }
  };

  const handleClick = async (dataPage) => {
    if (dataPage.newCuentaValue) {
      setCuentaSelect(dataPage.newCuentaValue);
      await loadList(dataPage.newCuentaValue);
    }
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setOpenConfirmDelete(true);
  };

  const handleDeleteSuccess = async () => {
    await loadList(cuentaSelect);
  };

  const loadList = async (cuentaSelectSid) => {
    const params = {
      account: cuentaSelectSid,
      action: "get-asg-cuenta",
    };
    setBlocking(true);
    const resp = await fetchData(url, context, params);
    setData(resp?.rows || []);
    setDataInstance(resp?.bodyAdd ? resp.bodyAdd.rows : []);
    setBlocking(false);
  };

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  const handleDelete = async () => {
    const params = {
      action: "DELETE",
      object: {
        id: rowToDelete.original ? rowToDelete.original.id : -1,
      },
    };
    setBlocking(true);
    setMsgAction("eliminando");
    let resp = await fetchData(url, context, params);
    setBlocking(false);
    setOpenConfirmDelete(false);
    await loadList(cuentaSelect);
  };

  const columns = useMemo(
    () => [
      { header: "Cuenta", accessorKey: "account", size: 120 },
      { header: "Cliente", accessorKey: "cliente", size: 150 },
      {
        header: "Estado",
        accessorKey: "estado",
        size: 120,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          const estado = renderedCellValue?.toLowerCase();
          /* eslint-enable react/prop-types */
          const config =
            estado === "enable"
              ? { label: "ENABLE", color: "#4caf50" }
              : estado === "disabled"
              ? { label: "DISABLED", color: "#f44336" }
              : { label: estado?.toUpperCase() || "—", color: "#9e9e9e" };
          return (
            <Box
              sx={{
                backgroundColor: `${config.color}20`,
                color: config.color,
                border: `1px solid ${config.color}60`,
                borderRadius: "12px",
                px: 1.5,
                py: 0.3,
                fontSize: 12,
                fontWeight: 700,
                textAlign: "center",
                display: "inline-block",
                minWidth: 80,
              }}
            >
              {config.label}
            </Box>
          );
        },
      },
      {
        header: "Instancias",
        accessorKey: "instances_array",
        size: 220,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          if (!renderedCellValue || !Array.isArray(renderedCellValue)) return "—";
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
              {renderedCellValue.map((inst, i) => (
                <Box key={i} sx={{ fontSize: 11, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: inst.principal ? "#4caf50" : "#1976d2",
                      flexShrink: 0,
                    }}
                  />
                  {inst.instance_id}
                </Box>
              ))}
            </Box>
          );
        },
        /* eslint-enable react/prop-types */
      },
      {
        header: "Fecha Creación",
        accessorKey: "fecha_creacion",
        size: 200,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          /* eslint-enable react/prop-types */
          if (!renderedCellValue) return "—";
          return new Date(renderedCellValue).toLocaleString("es-CO", {
            timeZone: "America/Bogota",
          });
        },
      },
      {
        header: "Horas Calientes",
        accessorKey: "horas_calientes",
        size: 150,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          if (!renderedCellValue || !Array.isArray(renderedCellValue)) return "—";
          /* eslint-enable react/prop-types */
          return renderedCellValue.join(", ");
        },
      },
      {
        header: "Horas Frías",
        accessorKey: "horas_frias",
        size: 120,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          if (!renderedCellValue || !Array.isArray(renderedCellValue)) return "—";
          /* eslint-enable react/prop-types */
          return renderedCellValue.join(", ");
        },
      },
      {
        header: "Apagado Forzoso",
        accessorKey: "horas_apagado_forzoso",
        size: 160,
        /* eslint-disable react/prop-types */
        Cell: ({ renderedCellValue }) => {
          if (!renderedCellValue || !Array.isArray(renderedCellValue)) return "—";
          /* eslint-enable react/prop-types */
          return renderedCellValue.join(", ");
        },
      },
      { header: "Hora Apagado Final", accessorKey: "hora_apagado_final", size: 160 },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={1}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} lg={12}>
            <Card>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox m={1}>
                  <Autocomplete
                    disablePortal
                    id="cuentas-resize"
                    options={cuentasResize.map((option) => option.title)}
                    renderInput={(params) => <TextField {...params} label="Cuentas" />}
                    onInputChange={(event, newCuentaValue) => {
                      handleClick({ newCuentaValue });
                    }}
                  />
                </MDBox>
              </Grid>
              {blocking ? (
                <AppLoading titleCustom={"Ec2-AutoScaling"} />
              ) : (
                <Grid item xs={12} md={12} lg={12}>
                  <MDBox m={1}>
                    <TableMaestros
                      arrayColumns={columns}
                      data={data}
                      handleOpenModal={handleOpenModal}
                      handleDelete={handleDeleteClick}
                      handleDeleteSuccess={handleDeleteSuccess}
                      height={320}
                    />
                  </MDBox>
                </Grid>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <UtilModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        renderView={renderView}
      />
      <Dialog open={openConfirmDelete} onClose={() => setOpenConfirmDelete(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`¿Está seguro que desea eliminar el registro de: ${rowToDelete?.original?.cliente}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)}>No</Button>
          <Button onClick={handleDelete} color="primary">
            Sí
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={openMonitor}
        onClose={handleCloseMonitor}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <ViewNewEdit
            row={rowSelect}
            dataInstance={dataInstance}
            handleCloseMonitor={handleCloseMonitor}
            cuentaSelect={cuentaSelect}
          />
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

const cuentasResize = [
  { title: "pdn" },
  { title: "pdn-m1" },
  { title: "pdn-m2" },
  { title: "pdn-m3" },
  { title: "pdn-m4" },
  { title: "pdn-m5" },
  { title: "pdn-m6" },
];

export default Example;
